/**
 * Chart-specific helper utilities for moving averages.
 * Uses granularityMetricConfig as the source of truth.
 */

import { getAvailableSmaPeriods, getAvailableEmaPeriods } from './granularityMetricConfig';

// Re-export MovingAverageConfig type for convenience
// The canonical definition is in IndicatorControls
export interface MovingAverageConfig {
  type: 'SMA' | 'EMA';
  period: number;
  color: string;
  enabled: boolean;
}

/**
 * Color palette for moving averages
 */
const MA_COLORS = {
  // SMA colors
  SMA_9: '#2196F3',    // Blue
  SMA_20: '#2196F3',   // Blue
  SMA_21: '#FF9800',   // Orange
  SMA_50: '#FF9800',   // Orange
  SMA_100: '#E91E63',  // Pink
  SMA_200: '#9C27B0',  // Purple

  // EMA colors
  EMA_9: '#4CAF50',    // Green
  EMA_20: '#00BCD4',   // Cyan
  EMA_21: '#F44336',   // Red
  EMA_50: '#4CAF50',   // Green
  EMA_100: '#CDDC39',  // Lime
  EMA_200: '#F44336',  // Red
};

/**
 * Get the color for a moving average based on type and period
 */
function getColorForMA(type: 'SMA' | 'EMA', period: number): string {
  const key = `${type}_${period}` as keyof typeof MA_COLORS;
  return MA_COLORS[key] || '#607D8B'; // Gray fallback
}

/**
 * Get default moving average configurations for a specific granularity.
 * This function returns ALL available MAs for the granularity with appropriate defaults enabled.
 *
 * @param granularityMins - The candle granularity in minutes
 * @returns Array of moving average configurations matching backend availability
 */
export function getDefaultMovingAverages(granularityMins: number): MovingAverageConfig[] {
  const smaPeriods = getAvailableSmaPeriods(granularityMins);
  const emaPeriods = getAvailableEmaPeriods(granularityMins);

  const configs: MovingAverageConfig[] = [];

  // Add all available SMAs
  smaPeriods.forEach((period, index) => {
    configs.push({
      type: 'SMA',
      period,
      color: getColorForMA('SMA', period),
      // Enable first 2 SMAs by default for most granularities
      enabled: index < 2 && granularityMins !== 1, // Don't enable SMAs by default for 1-minute
    });
  });

  // Add all available EMAs
  emaPeriods.forEach((period) => {
    // For 1-minute: Enable EMA 20 and 50 by default
    // For others: Keep EMAs disabled by default (user can enable)
    const isDefaultEnabled = granularityMins === 1 && (period === 20 || period === 50);

    configs.push({
      type: 'EMA',
      period,
      color: getColorForMA('EMA', period),
      enabled: isDefaultEnabled,
    });
  });

  return configs;
}

/**
 * Validate that a moving average configuration is available for the current granularity.
 * Returns true if the MA is valid, false otherwise.
 */
export function isMovingAverageValid(
  granularityMins: number,
  type: 'SMA' | 'EMA',
  period: number
): boolean {
  if (type === 'SMA') {
    return getAvailableSmaPeriods(granularityMins).includes(period);
  } else {
    return getAvailableEmaPeriods(granularityMins).includes(period);
  }
}

/**
 * Filter a list of moving average configs to only include those valid for the granularity.
 * Useful when restoring from session storage.
 */
export function filterValidMovingAverages(
  granularityMins: number,
  configs: MovingAverageConfig[]
): MovingAverageConfig[] {
  return configs.filter(config =>
    isMovingAverageValid(granularityMins, config.type, config.period)
  );
}
